import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './UnsavedChangesModal.css';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  /** True while the "Save changes" path is writing — shows a spinner and locks the buttons. */
  saving?: boolean;
  /** Write the pending edits, then leave. */
  onSave: () => void;
  /** Throw the pending edits away, then leave. */
  onDiscard: () => void;
  /** Stay on the page (the safe default). */
  onCancel: () => void;
}

/**
 * Shown when the estimator tries to leave the bid editor with edits that have not yet reached the
 * server. Gives an explicit choice — save them, discard them, or keep editing — rather than the
 * silent autosave-on-leave that happens when there is nothing outstanding.
 *
 * This covers IN-APP navigation only. A browser tab close/refresh cannot render a custom dialog, so
 * that case falls back to the native "Leave site?" prompt (see BidDetailPage's beforeunload).
 */
const UnsavedChangesModal = ({
  isOpen,
  saving = false,
  onSave,
  onDiscard,
  onCancel,
}: UnsavedChangesModalProps): JSX.Element | null => {
  if (!isOpen) return null;
  return (
    <div className="unsaved-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="unsaved-modal-title">
      <div className="unsaved-modal-content">
        <div className="unsaved-modal-icon">
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </div>
        <h3 id="unsaved-modal-title" className="unsaved-modal-title">
          Unsaved changes
        </h3>
        <p className="unsaved-modal-message">
          You have changes that haven&rsquo;t been saved yet. Save them before you leave, or discard
          them?
        </p>
        <div className="unsaved-modal-actions">
          <button
            type="button"
            className="unsaved-modal-btn unsaved-modal-save"
            onClick={onSave}
            disabled={saving}
            data-testid="unsaved-save"
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Saving&hellip;
              </>
            ) : (
              'Save changes'
            )}
          </button>
          <button
            type="button"
            className="unsaved-modal-btn unsaved-modal-discard"
            onClick={onDiscard}
            disabled={saving}
            data-testid="unsaved-discard"
          >
            Discard changes
          </button>
          <button
            type="button"
            className="unsaved-modal-btn unsaved-modal-cancel"
            onClick={onCancel}
            disabled={saving}
            data-testid="unsaved-cancel"
          >
            Keep editing
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnsavedChangesModal;
